import { useCallback, useMemo, useState } from 'react';
import { Button, Search, Tag } from '@carbon/react';
import {
  Add,
  CheckmarkFilled,
  ErrorFilled,
  SubtractAlt,
  TrashCan,
} from '@carbon/react/icons';
import type { ScenarioNavProps } from './types';
import styles from './scenario-nav.module.scss';

const GLOBAL_ID = 'global';

/**
 * Shared sidebar navigation for scenario forms.
 * Shows a global/details button at top, search input, add button,
 * and a scrollable list of area items with optional badges and overlap/error indicators.
 * @param {ScenarioNavProps} props - The component props
 * @return {JSX.Element} The sidebar navigation component
 */
export function ScenarioNav({
  items,
  selectedId,
  onSelect,
  onAdd,
  onDelete,
  globalLabel = 'Scenario Details',
  searchPlaceholder = 'Search areas...',
}: ScenarioNavProps): JSX.Element {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredItems = useMemo(
    () =>
      items.filter((item) =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [items, searchTerm]
  );

  /**
   * Handles search input change.
   * @param {React.ChangeEvent<HTMLInputElement>} e - The change event
   */
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value);
    },
    []
  );

  /**
   * Handles delete click with stopPropagation.
   * @param {React.MouseEvent} e - The click event
   * @param {number | string} id - The area ID to delete
   */
  const handleDelete = useCallback(
    (e: React.MouseEvent, id: number | string) => {
      e.stopPropagation();
      onDelete(id);
    },
    [onDelete]
  );

  return (
    <div className={styles.scenarioNav}>
      <button
        type="button"
        className={`${styles.globalButton} ${
          selectedId === GLOBAL_ID ? styles.selected : ''
        }`}
        onClick={() => onSelect(GLOBAL_ID)}
      >
        {globalLabel}
      </button>
      <div className={styles.searchRow}>
        <Search
          size="lg"
          placeholder={searchPlaceholder}
          labelText="Search areas"
          value={searchTerm}
          onChange={handleSearchChange}
        />
        <Button
          kind="primary"
          hasIconOnly
          renderIcon={Add}
          iconDescription="Add area"
          tooltipPosition="bottom"
          className={styles.addButton}
          onClick={onAdd}
        />
      </div>
      <div className={styles.areaList}>
        {filteredItems.map((item) => (
          <button
            key={item.id}
            type="button"
            className={`${styles.areaItem} ${
              selectedId === item.id ? styles.selected : ''
            }`}
            onClick={() => onSelect(item.id)}
          >
            <div className={styles.areaNameRow}>
              <span className={styles.areaName}>{item.name}</span>
              {item.badge && (
                <Tag type="gray" size="sm" className={styles.badgeTag}>
                  {item.badge}
                </Tag>
              )}
            </div>
            {(item.hasOverlap || item.error) && (
              <ErrorFilled
                className={styles.overlapIcon}
                aria-label={item.error}
              >
                {item.error && <title>{item.error}</title>}
              </ErrorFilled>
            )}
            {item.overlapResolved && (
              <CheckmarkFilled className={styles.resolvedIcon} />
            )}
            {item.isNew && (
              <Tag type="blue" size="sm" className={styles.newTag}>
                New
              </Tag>
            )}
            <Button
              kind="ghost"
              size="sm"
              hasIconOnly
              renderIcon={item.isNew ? SubtractAlt : TrashCan}
              iconDescription="Delete area"
              tooltipPosition="left"
              className={styles.deleteButton}
              onClick={(e: React.MouseEvent) => handleDelete(e, item.id)}
            />
          </button>
        ))}
      </div>
    </div>
  );
}
